export type CrawlStatus = "fresh" | "cooldown" | "blocked" | "sparse" | "new";

export type PriceSnapshot = {
  capturedAt: string;
  price: number;
  originalPrice?: number;
};

export type ProductListing = {
  listingKey: string;
  shopName: string;
  canonicalUrl: string;
  price: number | null;
  originalPrice?: number;
  crawlStatus: CrawlStatus;
  lastSuccessAt?: string;
};

export type Product = {
  productKey: string;
  canonicalProductKey: string;
  productName: string;
  shopName: string;
  canonicalUrl: string;
  imageUrl: string;
  identifierLabel: string;
  mergeStatus: "verified" | "probable" | "unmerged" | "needs_review";
  currency: "VND";
  latestPrice: number | null;
  latestOriginalPrice?: number;
  lastSuccessAt?: string;
  lastInterestAt: string;
  nextCrawlAt?: string;
  crawlStatus: CrawlStatus;
  interestCount30d: number;
  listings: ProductListing[];
  snapshots: PriceSnapshot[];
};

const now = new Date("2026-05-13T02:00:00.000Z");

const isoDaysAgo = (daysAgo: number, hour = 2): string => {
  const date = new Date(now);
  date.setUTCDate(now.getUTCDate() - daysAgo);
  date.setUTCHours(hour, 0, 0, 0);
  return date.toISOString();
};

const snapshotsFromPrices = (
  pricesOldestToNewest: number[],
  options: {
    originalOffset?: number;
    endDaysAgo?: number;
    gapEvery?: number;
  } = {},
): PriceSnapshot[] => {
  const endDaysAgo = options.endDaysAgo ?? 0;
  const gapEvery = options.gapEvery ?? 0;
  const filteredPrices = pricesOldestToNewest.filter(
    (_price, index) => gapEvery === 0 || (index + 1) % gapEvery !== 0,
  );
  return filteredPrices.map((price, index) => {
    const daysAgo = filteredPrices.length - index - 1 + endDaysAgo;
    return {
      capturedAt: isoDaysAgo(daysAgo),
      price,
      originalPrice: options.originalOffset ? price + options.originalOffset : undefined,
    };
  });
};

const iphoneSnapshots = snapshotsFromPrices(
  [
    12990000, 12990000, 12690000, 12490000, 12490000, 12290000, 12190000, 12190000,
    11990000, 11990000, 11890000, 11790000, 11790000, 11690000, 11690000, 11590000,
    11590000, 11390000, 11390000, 11290000, 11290000, 11190000, 11190000, 10990000,
  ],
  { originalOffset: 1400000 },
);

const airPodsSnapshots = snapshotsFromPrices(
  [
    3490000, 3490000, 3390000, 3290000, 3290000, 3190000, 3190000, 3090000, 3090000,
    2990000, 3090000, 3090000, 2990000, 2890000, 2890000, 2790000,
  ],
  { originalOffset: 400000, gapEvery: 5 },
);

const keyboardSnapshots = snapshotsFromPrices(
  [1690000, 1690000, 1590000, 1490000],
  { originalOffset: 300000, endDaysAgo: 2 },
);

export const trackedProducts: Product[] = [
  {
    productKey: "VN:882001:136790013",
    canonicalProductKey: "GTIN:0194252707068",
    productName: "iPhone 13 128GB - Midnight",
    shopName: "Apple Flagship Store",
    canonicalUrl: "https://shopee.vn/product/882001/136790013",
    imageUrl:
      "https://images.unsplash.com/photo-1591337676887-a217a6970a8a?auto=format&fit=crop&w=900&q=82",
    identifierLabel: "GTIN 0194252707068",
    mergeStatus: "verified",
    currency: "VND",
    latestPrice: iphoneSnapshots[iphoneSnapshots.length - 1].price,
    latestOriginalPrice: iphoneSnapshots[iphoneSnapshots.length - 1].originalPrice,
    lastSuccessAt: iphoneSnapshots[iphoneSnapshots.length - 1].capturedAt,
    lastInterestAt: "2026-05-13T01:34:00.000Z",
    nextCrawlAt: "2026-05-14T02:00:00.000Z",
    crawlStatus: "fresh",
    interestCount30d: 46,
    listings: [
      {
        listingKey: "VN:882001:136790013",
        shopName: "Apple Flagship Store",
        canonicalUrl: "https://shopee.vn/product/882001/136790013",
        price: iphoneSnapshots[iphoneSnapshots.length - 1].price,
        originalPrice: iphoneSnapshots[iphoneSnapshots.length - 1].originalPrice,
        crawlStatus: "fresh",
        lastSuccessAt: iphoneSnapshots[iphoneSnapshots.length - 1].capturedAt,
      },
      {
        listingKey: "VN:992144:771360111",
        shopName: "Mobile City VN",
        canonicalUrl: "https://shopee.vn/product/992144/771360111",
        price: 11290000,
        originalPrice: 12490000,
        crawlStatus: "fresh",
        lastSuccessAt: "2026-05-13T02:00:00.000Z",
      },
      {
        listingKey: "VN:441008:998310245",
        shopName: "Tech Outlet",
        canonicalUrl: "https://shopee.vn/product/441008/998310245",
        price: 10790000,
        originalPrice: 11990000,
        crawlStatus: "cooldown",
        lastSuccessAt: "2026-05-12T02:00:00.000Z",
      },
    ],
    snapshots: iphoneSnapshots,
  },
  {
    productKey: "VN:410224:779310044",
    canonicalProductKey: "GTIN:0195949059208",
    productName: "AirPods Pro 2 USB-C",
    shopName: "Audio Market VN",
    canonicalUrl: "https://shopee.vn/product/410224/779310044",
    imageUrl:
      "https://images.unsplash.com/photo-1600294037681-c80b4cb5b434?auto=format&fit=crop&w=900&q=82",
    identifierLabel: "GTIN 0195949059208",
    mergeStatus: "verified",
    currency: "VND",
    latestPrice: airPodsSnapshots[airPodsSnapshots.length - 1].price,
    latestOriginalPrice: airPodsSnapshots[airPodsSnapshots.length - 1].originalPrice,
    lastSuccessAt: airPodsSnapshots[airPodsSnapshots.length - 1].capturedAt,
    lastInterestAt: "2026-05-12T15:20:00.000Z",
    nextCrawlAt: "2026-05-14T02:00:00.000Z",
    crawlStatus: "cooldown",
    interestCount30d: 28,
    listings: [
      {
        listingKey: "VN:410224:779310044",
        shopName: "Audio Market VN",
        canonicalUrl: "https://shopee.vn/product/410224/779310044",
        price: airPodsSnapshots[airPodsSnapshots.length - 1].price,
        originalPrice: airPodsSnapshots[airPodsSnapshots.length - 1].originalPrice,
        crawlStatus: "cooldown",
        lastSuccessAt: airPodsSnapshots[airPodsSnapshots.length - 1].capturedAt,
      },
      {
        listingKey: "VN:901100:222889331",
        shopName: "Apple Audio Store",
        canonicalUrl: "https://shopee.vn/product/901100/222889331",
        price: 2890000,
        originalPrice: 3290000,
        crawlStatus: "fresh",
        lastSuccessAt: "2026-05-13T02:00:00.000Z",
      },
    ],
    snapshots: airPodsSnapshots,
  },
  {
    productKey: "VN:660012:194220875",
    canonicalProductKey: "BRAND_MODEL:keychron:k2-wireless",
    productName: "Keychron K2 Wireless Keyboard",
    shopName: "Gear Lab",
    canonicalUrl: "https://shopee.vn/product/660012/194220875",
    imageUrl:
      "https://images.unsplash.com/photo-1587829741301-dc798b83add3?auto=format&fit=crop&w=900&q=82",
    identifierLabel: "Brand/Model Keychron K2",
    mergeStatus: "probable",
    currency: "VND",
    latestPrice: keyboardSnapshots[keyboardSnapshots.length - 1].price,
    latestOriginalPrice: keyboardSnapshots[keyboardSnapshots.length - 1].originalPrice,
    lastSuccessAt: keyboardSnapshots[keyboardSnapshots.length - 1].capturedAt,
    lastInterestAt: "2026-05-12T03:42:00.000Z",
    nextCrawlAt: "2026-05-14T02:00:00.000Z",
    crawlStatus: "sparse",
    interestCount30d: 6,
    listings: [
      {
        listingKey: "VN:660012:194220875",
        shopName: "Gear Lab",
        canonicalUrl: "https://shopee.vn/product/660012/194220875",
        price: keyboardSnapshots[keyboardSnapshots.length - 1].price,
        originalPrice: keyboardSnapshots[keyboardSnapshots.length - 1].originalPrice,
        crawlStatus: "sparse",
        lastSuccessAt: keyboardSnapshots[keyboardSnapshots.length - 1].capturedAt,
      },
      {
        listingKey: "VN:772211:450019222",
        shopName: "Keyboard Studio",
        canonicalUrl: "https://shopee.vn/product/772211/450019222",
        price: 1540000,
        originalPrice: 1790000,
        crawlStatus: "fresh",
        lastSuccessAt: "2026-05-13T02:00:00.000Z",
      },
    ],
    snapshots: keyboardSnapshots,
  },
  {
    productKey: "VN:120004:880019004",
    canonicalProductKey: "UNMERGED:VN:120004:880019004",
    productName: "Sony WH-1000XM5",
    shopName: "Sound House",
    canonicalUrl: "https://shopee.vn/product/120004/880019004",
    imageUrl:
      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=900&q=82",
    identifierLabel: "Unmerged listing",
    mergeStatus: "unmerged",
    currency: "VND",
    latestPrice: 7190000,
    latestOriginalPrice: 8290000,
    lastSuccessAt: "2026-05-10T02:00:00.000Z",
    lastInterestAt: "2026-05-13T00:20:00.000Z",
    nextCrawlAt: "2026-05-13T02:00:00.000Z",
    crawlStatus: "blocked",
    interestCount30d: 19,
    listings: [
      {
        listingKey: "VN:120004:880019004",
        shopName: "Sound House",
        canonicalUrl: "https://shopee.vn/product/120004/880019004",
        price: 7190000,
        originalPrice: 8290000,
        crawlStatus: "blocked",
        lastSuccessAt: "2026-05-10T02:00:00.000Z",
      },
    ],
    snapshots: snapshotsFromPrices([7790000, 7590000, 7490000, 7290000, 7190000], {
      originalOffset: 1100000,
      endDaysAgo: 3,
    }),
  },
];

export const createFirstTimeProduct = (productKey: string, canonicalUrl: string): Product => ({
  productKey,
  canonicalProductKey: `UNMERGED:${productKey}`,
  productName: "New Shopee listing",
  shopName: "Pending crawl",
  canonicalUrl,
  imageUrl:
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=82",
  identifierLabel: "Awaiting identifiers",
  mergeStatus: "unmerged",
  currency: "VND",
  latestPrice: null,
  lastInterestAt: now.toISOString(),
  nextCrawlAt: now.toISOString(),
  crawlStatus: "new",
  interestCount30d: 1,
  listings: [
    {
      listingKey: productKey,
      shopName: "Pending crawl",
      canonicalUrl,
      price: null,
      crawlStatus: "new",
    },
  ],
  snapshots: [],
});
